export const ROLES = {
  GLOBAL_ADMIN: 'global_admin',
  IT_ADMIN: 'it_admin',
  PARTS_SERVICE_COORDINATOR: 'parts_service_coordinator',
  SHIPPER_RECEIVER: 'shipper_receiver',
  SALES: 'sales',
  MECHANIC: 'mechanic',
  DRIVER: 'driver',
};

export const ROLE_OPTIONS = [
  { value: ROLES.GLOBAL_ADMIN, label: 'Global Admin' },
  { value: ROLES.IT_ADMIN, label: 'IT Admin' },
  { value: ROLES.PARTS_SERVICE_COORDINATOR, label: 'Parts and Service Coordinator' },
  { value: ROLES.SHIPPER_RECEIVER, label: 'Shipper / Receiver' },
  { value: ROLES.SALES, label: 'Sales' },
  { value: ROLES.MECHANIC, label: 'Mechanic' },
  { value: ROLES.DRIVER, label: 'Driver' },
];

export const MODULES = {
  DASHBOARD: 'dashboard',
  VEHICLES: 'vehicles',
  PARTS: 'parts',
  MOVEMENTS: 'movements',
  DRIVERS: 'drivers',
  SERVICE_LOGS: 'service_logs',
};

export const MODULE_OPTIONS = [
  { value: MODULES.DASHBOARD, label: 'Dashboard', description: 'Fleet overview, dashboard cards, and master export.' },
  { value: MODULES.VEHICLES, label: 'Vehicles', description: 'Vehicle list, categories, vehicle details, photos, glove box, and inspections.' },
  { value: MODULES.PARTS, label: 'Parts / Inventory', description: 'Parts inventory, quantities, reminders, photos, invoices, and vehicle-attached parts.' },
  { value: MODULES.MOVEMENTS, label: 'Inventory Transfer', description: 'Inventory transfer history and reports.' },
  { value: MODULES.DRIVERS, label: 'Drivers', description: 'Driver inspections, defect reports, fuel records, and trip logs.' },
  { value: MODULES.SERVICE_LOGS, label: 'Service Logs', description: 'Service logs, repairs, and scheduled maintenance reminders.' },
];

const FULL_ACCESS_ROLES = [
  ROLES.GLOBAL_ADMIN,
  ROLES.IT_ADMIN,
];

const FULL_OPERATIONAL_ACCESS_ROLES = [
  ROLES.SHIPPER_RECEIVER,
  ROLES.SALES,
];

export function normalizeRole(role) {
  const normalized = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (normalized === 'admin') return ROLES.GLOBAL_ADMIN;
  if (normalized === 'user' || normalized === 'parts_and_service_coordinator') {
    return ROLES.PARTS_SERVICE_COORDINATOR;
  }
  return Object.values(ROLES).includes(normalized) ? normalized : ROLES.DRIVER;
}

export function roleLabel(role) {
  const normalized = normalizeRole(role);
  return ROLE_OPTIONS.find((option) => option.value === normalized)?.label || 'Driver';
}

export function canManageAccess(role) {
  return FULL_ACCESS_ROLES.includes(normalizeRole(role));
}

export function canManageTruckScheduling(role) {
  return [ROLES.GLOBAL_ADMIN, ROLES.IT_ADMIN, ROLES.SHIPPER_RECEIVER].includes(normalizeRole(role));
}

export function hasFullAccess(role) {
  const normalized = normalizeRole(role);
  return canManageAccess(normalized) || FULL_OPERATIONAL_ACCESS_ROLES.includes(normalized);
}

export function defaultModulePermissions(role) {
  const normalized = normalizeRole(role);
  const allAccess = Object.fromEntries(MODULE_OPTIONS.map((module) => [module.value, true]));
  const noAccess = Object.fromEntries(MODULE_OPTIONS.map((module) => [module.value, false]));

  if (hasFullAccess(normalized)) return allAccess;
  if (normalized === ROLES.PARTS_SERVICE_COORDINATOR) return allAccess;
  if (normalized === ROLES.MECHANIC) {
    return {
      ...noAccess,
      [MODULES.DRIVERS]: true,
      [MODULES.SERVICE_LOGS]: true,
    };
  }
  if (normalized === ROLES.DRIVER) return { ...noAccess, [MODULES.DRIVERS]: true };
  return noAccess;
}

export function normalizeModulePermissions(role, permissions) {
  const defaults = defaultModulePermissions(role);
  if (canManageAccess(role)) return defaults;
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) return defaults;

  return Object.fromEntries(
    MODULE_OPTIONS.map((module) => [
      module.value,
      defaults[module.value] || permissions[module.value] === true,
    ]),
  );
}

export function hasModuleAccess(userOrRole, moduleName, permissions) {
  const role = typeof userOrRole === 'object' ? userOrRole?.role : userOrRole;
  const storedPermissions = typeof userOrRole === 'object' ? userOrRole?.module_permissions : permissions;
  const normalizedPermissions = normalizeModulePermissions(role, storedPermissions);
  return normalizedPermissions[moduleName] === true;
}

export function moduleForPath(pathname = '') {
  const path = pathname || '/dashboard';
  if (path === '/' || path === '/dashboard') return MODULES.DASHBOARD;
  if (path === '/trucks' || path.startsWith('/trucks') || path.startsWith('/truck/') || path === '/live-gps') return MODULES.VEHICLES;
  if (path === '/parts' || path.startsWith('/parts') || path === '/inventory') return MODULES.PARTS;
  if (path === '/movements' || path.startsWith('/movements')) return MODULES.MOVEMENTS;
  if (path === '/drivers' || path.startsWith('/drivers/check-in')) return MODULES.DRIVERS;
  if (path === '/service-logs' || path.startsWith('/service-logs') || path === '/maintenance-reminders') return MODULES.SERVICE_LOGS;
  if (path === '/truck-scheduling' || path.startsWith('/truck-scheduling/')) return 'truck_scheduling';
  if (path === '/mechanic-defects' || path.startsWith('/mechanic-defects/') || path === '/resolved-defects' || path.startsWith('/resolved-defects/')) return 'mechanic_defects';
  if (path === '/users' || path.startsWith('/users')) return 'user_management';
  return null;
}

export function canAccessPath(userOrRole, pathname = '', permissions) {
  const role = typeof userOrRole === 'object' ? userOrRole?.role : userOrRole;
  const moduleName = moduleForPath(pathname);
  if (moduleName === 'user_management') return canManageAccess(role);
  if (moduleName === 'truck_scheduling') return canManageTruckScheduling(role);
  if (moduleName === 'mechanic_defects') {
    return [ROLES.MECHANIC, ROLES.GLOBAL_ADMIN, ROLES.PARTS_SERVICE_COORDINATOR].includes(normalizeRole(role));
  }
  if (!moduleName) return false;
  return hasModuleAccess(userOrRole, moduleName, permissions);
}

export function defaultPathForUser(userOrRole, permissions) {
  const role = typeof userOrRole === 'object' ? userOrRole?.role : userOrRole;
  if (canManageAccess(role)) return '/dashboard';

  const normalizedPermissions = normalizeModulePermissions(
    role,
    typeof userOrRole === 'object' ? userOrRole?.module_permissions : permissions,
  );

  const firstEnabled = MODULE_OPTIONS.find((module) => normalizedPermissions[module.value]);
  if (!firstEnabled) return '/drivers';

  const pathByModule = {
    [MODULES.DASHBOARD]: '/dashboard',
    [MODULES.VEHICLES]: '/trucks',
    [MODULES.PARTS]: '/parts',
    [MODULES.MOVEMENTS]: '/movements',
    [MODULES.DRIVERS]: '/drivers',
    [MODULES.SERVICE_LOGS]: '/service-logs',
  };

  return pathByModule[firstEnabled.value] || '/drivers';
}

export function defaultPathForRole(role) {
  return defaultPathForUser(role);
}
